import { createContext, useContext, useEffect, useState, useCallback } from "react"
import { useAuth } from "@/lib/auth/auth-context"
import { fetchUserSubAccounts } from "@/lib/services/sub-accounts"
import type { SubAccount } from "@/lib/types"

interface AccountSelectionContextType {
  selectedAccountId: string // "all" or specific account ID
  setSelectedAccountId: (accountId: string) => void
  selectedAccount: SubAccount | null // null when "all" is selected
  subAccounts: SubAccount[]
  loading: boolean
  refreshSubAccounts: () => Promise<void>
}

const AccountSelectionContext = createContext<AccountSelectionContextType>({
  selectedAccountId: "all",
  setSelectedAccountId: () => {},
  selectedAccount: null,
  subAccounts: [],
  loading: true,
  refreshSubAccounts: async () => {},
})

const STORAGE_KEY = "effortless-spend-sense:selected-account"

export function AccountSelectionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [selectedAccountId, setSelectedAccountIdState] = useState<string>("all")
  const [selectedAccount, setSelectedAccount] = useState<SubAccount | null>(null)
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([])
  const [loading, setLoading] = useState(true)

  // Load selected account from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      console.log('📂 [ACCOUNT-CONTEXT] Loaded from localStorage:', stored)
      setSelectedAccountIdState(stored)
    }
  }, [])

  // Fetch sub-accounts for the current user
  const fetchSubAccounts = useCallback(async () => {
    if (!user?.id) {
      setSubAccounts([])
      setSelectedAccount(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const accounts = await fetchUserSubAccounts(user.id)

      // Filter to only show active accounts
      const activeAccounts = accounts.filter(acc => acc.isActive)

      console.log('✅ [ACCOUNT-CONTEXT] Sub-accounts fetched:', activeAccounts.length)
      setSubAccounts(activeAccounts)
    } catch (error) {
      console.error('❌ [ACCOUNT-CONTEXT] Error fetching sub-accounts:', error)
      setSubAccounts([])
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  // Fetch sub-accounts when user changes
  useEffect(() => {
    fetchSubAccounts()
  }, [fetchSubAccounts])

  // Update selectedAccount object when selectedAccountId or subAccounts change
  useEffect(() => {
    if (selectedAccountId === "all") {
      setSelectedAccount(null)
    } else {
      const account = subAccounts.find(acc => acc.id === selectedAccountId)
      setSelectedAccount(account || null)

      // If selected account doesn't exist (deleted/deactivated), reset to "all"
      if (!account && selectedAccountId !== "all" && subAccounts.length > 0) {
        console.log('⚠️ [ACCOUNT-CONTEXT] Selected account not found, resetting to "all"')
        setSelectedAccountIdState("all")
        localStorage.setItem(STORAGE_KEY, "all")
      }
    }
  }, [selectedAccountId, subAccounts])

  // Wrapper to save to localStorage when selection changes
  const setSelectedAccountId = useCallback((accountId: string) => {
    console.log('🔄 [ACCOUNT-CONTEXT] Account selection changed:', {
      from: selectedAccountId,
      to: accountId,
      timestamp: new Date().toISOString()
    })

    setSelectedAccountIdState(accountId)
    localStorage.setItem(STORAGE_KEY, accountId)
  }, [selectedAccountId])

  return (
    <AccountSelectionContext.Provider
      value={{
        selectedAccountId,
        setSelectedAccountId,
        selectedAccount,
        subAccounts,
        loading,
        refreshSubAccounts: fetchSubAccounts,
      }}
    >
      {children}
    </AccountSelectionContext.Provider>
  )
}

export const useAccountSelection = () => {
  const context = useContext(AccountSelectionContext)
  if (!context) {
    throw new Error("useAccountSelection must be used within AccountSelectionProvider")
  }
  return context
}
